import { IPassengerInfo, IRideDto } from "../../dto";
import { HubSegmentEnum } from "../../enums";
import { isValidPositiveNumber } from "../numbers.helpers";

export enum HubRideActionKind {
  Archive = "Archive",
  Cancel = "Cancel",
  Modify = "Modify",
}

export interface IRideHubActionContext {
  hubSegment: HubSegmentEnum;
  relatedRideIds: number[];
  passengersCount: number;
}

export interface IRideHubPresence {
  hasMain: boolean;
  hasPersonal: boolean;
  hubSegment: HubSegmentEnum | undefined;
  hasCompanionPassengers: boolean;
}

export interface IRelatedHubRideIdsBySegment {
  relatedMainRideIds: number[];
  relatedPersonalRideIds: number[];
}

const isHubSegment = (value: string | undefined): value is HubSegmentEnum =>
  value === HubSegmentEnum.Main || value === HubSegmentEnum.Personal;

/** Native hub leg passenger: has segment tag and/or main/personal ride pairing links. */
export const isNativeHubPassenger = (
  passenger: Pick<
    IPassengerInfo,
    "hubSegment" | "rideMainId" | "ridePersonalId"
  >,
): boolean =>
  isHubSegment(passenger.hubSegment) ||
  isValidPositiveNumber(passenger.rideMainId) ||
  isValidPositiveNumber(passenger.ridePersonalId);

/**
 * Passenger seated on a hub ride without being a hub booking (e.g. drag-joined via rideId).
 * Shown only when the ride mixes native hub passengers with at least one non-hub passenger.
 */
export const isHubRideCompanionPassenger = ({
  passenger,
  rideDto,
}: {
  passenger: Pick<
    IPassengerInfo,
    "hubSegment" | "rideMainId" | "ridePersonalId"
  >;
  rideDto: Partial<IRideDto> | undefined;
}): boolean => {
  if (!hasHubRideCompanionPassengers(rideDto)) {
    return false;
  }

  return !isNativeHubPassenger(passenger);
};

export const hasHubRideCompanionPassengers = (
  rideDto: Partial<IRideDto> | undefined,
): boolean => {
  const passengers = rideDto?.metadata?.passengersInfo ?? [];
  if (!isHubSegment(rideDto?.metadata?.hubSegment) || passengers.length < 2) {
    return false;
  }

  let hasNativeHub = false;
  let hasNonHub = false;
  for (const passenger of passengers) {
    if (isNativeHubPassenger(passenger)) {
      hasNativeHub = true;
    } else {
      hasNonHub = true;
    }
    if (hasNativeHub && hasNonHub) {
      return true;
    }
  }

  return false;
};

const resolveRideHubSegment = ({
  rideHubSegment,
  hasMain,
  hasPersonal,
}: {
  rideHubSegment: HubSegmentEnum | undefined;
  hasMain: boolean;
  hasPersonal: boolean;
}): HubSegmentEnum | undefined => {
  if (rideHubSegment) {
    return rideHubSegment;
  }

  if (hasMain && !hasPersonal) {
    return HubSegmentEnum.Main;
  }

  if (hasPersonal && !hasMain) {
    return HubSegmentEnum.Personal;
  }

  if (hasMain && hasPersonal) {
    // Prefer Main for paired-ride warning wording when ride-level hubSegment is missing.
    return HubSegmentEnum.Main;
  }

  return undefined;
};

export const getRideHubPresence = (
  rideDto: Partial<IRideDto> | undefined,
): IRideHubPresence | null => {
  if (!rideDto) {
    return null;
  }

  const metadata = rideDto.metadata;
  const passengers = metadata?.passengersInfo ?? [];
  const rideHubSegment = isHubSegment(metadata?.hubSegment)
    ? metadata.hubSegment
    : undefined;

  const hasMain =
    rideHubSegment === HubSegmentEnum.Main ||
    passengers.some(p => p.hubSegment === HubSegmentEnum.Main);
  const hasPersonal =
    rideHubSegment === HubSegmentEnum.Personal ||
    passengers.some(p => p.hubSegment === HubSegmentEnum.Personal);

  if (!hasMain && !hasPersonal) {
    return null;
  }

  const presence: IRideHubPresence = {
    hasMain,
    hasPersonal,
    hubSegment: resolveRideHubSegment({
      rideHubSegment,
      hasMain,
      hasPersonal,
    }),
    hasCompanionPassengers: hasHubRideCompanionPassengers(rideDto),
  };
  return presence;
};

export const collectRelatedHubRideIdsBySegment = (
  rideDto: Partial<IRideDto> | undefined,
): IRelatedHubRideIdsBySegment => {
  const currentId = rideDto?.id;
  const metadata = rideDto?.metadata;
  const passengers = metadata?.passengersInfo ?? [];
  const mainIds = new Set<number>();
  const personalIds = new Set<number>();

  if (
    isValidPositiveNumber(metadata?.relatedMainRideId) &&
    metadata.relatedMainRideId !== currentId
  ) {
    mainIds.add(metadata.relatedMainRideId);
  }

  if (
    isValidPositiveNumber(metadata?.relatedHubRideId) &&
    metadata.relatedHubRideId !== currentId
  ) {
    personalIds.add(metadata.relatedHubRideId);
  }

  for (const passenger of passengers) {
    if (
      isValidPositiveNumber(passenger.rideMainId) &&
      passenger.rideMainId !== currentId
    ) {
      mainIds.add(passenger.rideMainId);
    }

    if (
      isValidPositiveNumber(passenger.ridePersonalId) &&
      passenger.ridePersonalId !== currentId
    ) {
      personalIds.add(passenger.ridePersonalId);
    }
  }

  const relatedIds: IRelatedHubRideIdsBySegment = {
    relatedMainRideIds: [...mainIds],
    relatedPersonalRideIds: [...personalIds],
  };
  return relatedIds;
};

export const getRideHubActionContext = (
  rideDto: Partial<IRideDto> | undefined,
): IRideHubActionContext | null => {
  const presence = getRideHubPresence(rideDto);
  if (!presence?.hubSegment) {
    return null;
  }

  const { relatedMainRideIds, relatedPersonalRideIds } =
    collectRelatedHubRideIdsBySegment(rideDto);

  const context: IRideHubActionContext = {
    hubSegment: presence.hubSegment,
    relatedRideIds: [
      ...new Set([...relatedMainRideIds, ...relatedPersonalRideIds]),
    ],
    passengersCount: rideDto?.metadata?.passengersInfo?.length ?? 0,
  };
  return context;
};

export const getHubRideActionTranslationKey = ({
  actionKind,
  hubSegment,
}: {
  actionKind: HubRideActionKind;
  hubSegment: HubSegmentEnum;
}): string => {
  if (actionKind === HubRideActionKind.Modify) {
    return "ride.HUB_RIDE_MODIFY_MESSAGE";
  }

  if (hubSegment === HubSegmentEnum.Personal) {
    return actionKind === HubRideActionKind.Archive
      ? "ride.HUB_RIDE_ARCHIVE_PERSONAL_MESSAGE"
      : "ride.HUB_RIDE_CANCEL_PERSONAL_MESSAGE";
  }

  return actionKind === HubRideActionKind.Archive
    ? "ride.HUB_RIDE_ARCHIVE_MAIN_MESSAGE"
    : "ride.HUB_RIDE_CANCEL_MAIN_MESSAGE";
};

export const getHubRideActionTranslationParams = (
  hubContext: IRideHubActionContext,
): Record<string, string | number> => {
  const relatedRideIdsLabel = hubContext.relatedRideIds.join(", ");
  // Warn about the opposite hub leg (main ride → personal message, and vice versa).
  const relatedSegmentLabelKey =
    hubContext.hubSegment === HubSegmentEnum.Main
      ? "ride.HUB_PERSONAL"
      : "ride.HUB_MAIN";

  const params: Record<string, string | number> = {
    relatedRideIds: relatedRideIdsLabel,
    passengersCount: hubContext.passengersCount,
    segmentLabelKey:
      hubContext.hubSegment === HubSegmentEnum.Main
        ? "ride.HUB_MAIN"
        : "ride.HUB_PERSONAL",
    relatedSegmentLabelKey,
  };
  return params;
};

export const collectHubRideActionContexts = (
  rides: (Partial<IRideDto> | undefined)[],
): IRideHubActionContext[] => {
  return rides
    .map(ride => getRideHubActionContext(ride))
    .filter((context): context is IRideHubActionContext => context !== null);
};
